// The i18next literal-string lint only runs on .tsx, so these presentational literals live here.

import type { TFunction } from "i18next"

import type { EmailSendStatus } from "@/generated/api/types.generated"

export { TONE } from "@/shared-module/components"

/**
 * The namespace every view in this feature reads. Its strings are a third of the service's
 * catalogue and only these pages need them, so they load as their own chunk rather than on every
 * page. Labels shared with the rest of the app still resolve, through `fallbackNS`.
 */
export const CREDIT_REGISTRATION_NS = "credit-registration" as const

/** The `t` that namespace gives: helpers that build labels take this, not the service-wide one. */
export type CreditRegistrationTFunction = TFunction<typeof CREDIT_REGISTRATION_NS>

/** How much of a uuid an operator needs to recognise a row in a chip or a target label. */
export const ID_PREFIX_LENGTH = 8

export const STACKED = "stacked" as const

/** `TableColumn.align` for numeric columns. */
export const ALIGN_END = "end" as const

/** `TableColumn.align` for a column of icons or short controls. */
export const ALIGN_CENTER = "center" as const

/** `TableColumn.sortDirection` for the column a table is currently sorted by. */
export const SORT_ASCENDING = "ascending" as const

/** `QueryResult.refreshIndicator` for polled views: a poll must not blank or freeze the page. */
export const QUIET_REFRESH = "quiet" as const

/** `RelativeTime.absoluteTime` for tables carrying several time columns. */
export const TIME_IN_TITLE = "title" as const

/** `RelativeTime.absoluteTime` for a date the reader has to reconcile rather than watch. */
export const TIME_COMPACT = "compact" as const

/** `RelativeTime.absoluteTime` for a completion or registration date, which wants no time of day. */
export const TIME_DATE = "date" as const

/** `RelativeTime.absoluteTime` for an age the reader compares against a threshold. */
export const TIME_DURATION = "duration" as const

/** `SupportMailLink.appearance` for one inside a sentence rather than beside the buttons. */
export const SUPPORT_MAIL_INLINE = "link" as const

/** `Table.density` for the operator tables: logs and queues, where rows matter more than air. */
export const DENSITY_COMPACT = "compact" as const

/** `Link.appearance` for a link that is the whole content of a table cell. */
export const LINK_QUIET = "quiet" as const

/** `Link.appearance` for a link wrapping content that sets its own colour, e.g. a badge. */
export const LINK_INHERIT = "inherit" as const

/** `Disclosure.variant` for one that sits in running text rather than as a box of its own. */
export const PLAIN_DISCLOSURE = "plain" as const

/** `RegistrationStatusState`s a surface names directly, to group rows or to badge a stage. */
export const STATE_ACTION_NEEDED = "action-needed" as const
export const STATE_FAILED = "failed" as const
export const STATE_SUPERSEDED = "superseded" as const

/** `Badge.size` for a badge that is a dense table cell's content rather than a chip in a row. */
export const BADGE_COMPACT = "compact" as const

/** The `EmailSendStatus` a send cell badges as a danger. */
export const SEND_FAILED: EmailSendStatus = "send_failed"

/** `Button.variant` for the one action a surface leads with, and for the rest beside it. */
export const BUTTON_PRIMARY = "primary" as const
export const BUTTON_SECONDARY = "secondary" as const

/** `Button.size` for an action that sits inside a card or a row rather than under a form. */
export const BUTTON_SMALL = "small" as const

/** `Button.iconPosition` for an icon after the label, like the arrow on a link that leaves the site. */
export const ICON_AT_END = "end" as const

/** `Button.variant` for a control that reads as a link but changes what is on screen. */
export const BUTTON_TERTIARY = "tertiary" as const

/** `Button.variant` for an action that ends something and cannot be taken back. */
export const BUTTON_DANGER = "danger" as const

/** `Table.responsive` for an operator table that becomes labelled cards on a phone. */
export const TABLE_STACK = "stack" as const

/**
 * `Pagination.itemsPerPageOptions` for the operator tables. Small on purpose: these rows carry
 * links, meters and badges, and the page turn is cheaper than a thousand of them.
 */
export const ADMIN_PAGE_SIZE_OPTIONS = [25, 50, 100]

/** `useDateFormatter` options for a chart axis, where the year is in the heading and not the tick. */
export const DAY_AND_MONTH_FORMAT = { day: "numeric", month: "short" } as const

/** Where a student can check that credits actually arrived. */
export const SISU_URL = "https://sisu.helsinki.fi/student/frontpage"

/** Where a student sees all their registered studies, a couple of days after Sisu has them. */
export const OMA_OPINTOPOLKU_URL = "https://opintopolku.fi/oma-opintopolku/"

const SISU_ENROLMENT_INSTRUCTIONS_URL_FI =
  "https://studies.helsinki.fi/ohjeet/artikkeli/sisu-ohje-ilmoittautuminen-opetukseen-ja-tentteihin"
const SISU_ENROLMENT_INSTRUCTIONS_URL_SV =
  "https://studies.helsinki.fi/instruktioner/artikel/sisu-instruktion-anmalning-till-undervisning-och-tentamen"
const SISU_ENROLMENT_INSTRUCTIONS_URL_EN =
  "https://studies.helsinki.fi/instructions/article/sisu-instructions-registering-teaching-and-exams"

/** How a University of Helsinki student enrols on a course in Sisu, which has no per-course link. */
export const sisuEnrolmentInstructionsUrl = (language: string): string =>
  /^fi(?:-|$)/.test(language)
    ? SISU_ENROLMENT_INSTRUCTIONS_URL_FI
    : /^sv(?:-|$)/.test(language)
      ? SISU_ENROLMENT_INSTRUCTIONS_URL_SV
      : SISU_ENROLMENT_INSTRUCTIONS_URL_EN

/** What Suomi.fi says about identifying with a token issued by another European country. */
export const SUOMI_FI_EIDAS_URL =
  "https://www.suomi.fi/instructions-and-support/identification/information-on-identification-tokens-used-in-suomi-fi-e-identification/using-the-identification-tokens-of-other-european-countries-in-finland"

/** What Suomi.fi says about the identification tokens it accepts, of which eIDAS is one. */
export const SUOMI_FI_IDENTIFICATION_URL =
  "https://www.suomi.fi/instructions-and-support/identification/information-on-identification-tokens-used-in-suomi-fi-e-identification"

/** `RadioGroup.variant` for a short closed question whose answers read as a choice, not a list. */
export const SEGMENTED = "segmented" as const

/** The two answers to which university relationship a student has; the wire values of the enum. */
export const UNIVERSITY_OF_HELSINKI = "university_of_helsinki" as const
export const OPEN_UNIVERSITY = "open_university" as const

// The Open University only publishes this page in Finnish and English; other languages fall back to
// the English version.
const OPEN_UNIVERSITY_ENROLMENT_INFO_URL_FI =
  "https://www.helsinki.fi/fi/hakeminen-ja-opetus/avoin-yliopisto/ilmoittautuminen-ja-opintomaksut"
const OPEN_UNIVERSITY_ENROLMENT_INFO_URL_EN =
  "https://www.helsinki.fi/en/admissions-and-education/open-university/enrollment-and-study-fees"

/** How the Open University explains enrolling, for a module with no enrolment link of its own. */
export const openUniversityEnrolmentInfoUrl = (language: string): string =>
  /^fi(?:-|$)/.test(language)
    ? OPEN_UNIVERSITY_ENROLMENT_INFO_URL_FI
    : OPEN_UNIVERSITY_ENROLMENT_INFO_URL_EN

/** Kept in one place so every page that names it can change together. */
export const SUPPORT_EMAIL = "mooc@helsinki.fi"
